import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  deleteLatestAllocation,
  generateWaterfallJournalEntry,
  getAllocations,
  getWaterfallJournalEntryMonthsSummary,
} from '@/services/waterfall'
import type { AllocationResponse, MemberAmounts, WaterfallJeMonthsSummaryMonth } from '@/types/waterfall'
import { MEMBERS } from '@/types/waterfall'
import { useMaxMd } from '@/hooks/useMaxMd'
import { usePageAccess } from '@/hooks/usePageAccess'
import { formatApiError } from '@/utils/apiError'
import { allocationAmountClassName } from '@/utils/allocationFormat'
import { toast } from '@/store/toastStore'
import WaterfallTabHeader from './WaterfallTabHeader'
import WaterfallJournalEntryCard from './WaterfallJournalEntryCard'
import DistributionJeRowButton from './DistributionJeRowButton'
import {
  JE_COL_CLASS,
  MOBILE_NOTE_TOGGLE_COL_CLASS,
  MOBILE_NUM_COL_CLASS,
  NOTE_COL_CLASS,
  NUM_COL_CLASS,
  STICKY_HEAD_CLASS,
  STICKY_NUM_CLASS,
  STICKY_NUM_HEAD_CLASS,
  STICKY_NUM_TOTALS_CLASS,
  STICKY_PERIOD_CLASS,
  STICKY_PERIOD_HEAD_CLASS,
  STICKY_PERIOD_HEAD_MOBILE_CLASS,
  STICKY_PERIOD_MOBILE_CLASS,
  STICKY_PERIOD_TOTALS_CLASS,
  STICKY_PERIOD_TOTALS_MOBILE_CLASS,
  STICKY_TOTALS_CLASS,
  TOTAL_COL_BORDER,
} from './distributionHistoryTableLayout'
import { parseJeGenerateError } from './allocationMonthReadinessUtils'
import { monthFromIsoDate } from './waterfallJeStatus'

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

function memberTotal(amounts: MemberAmounts): number {
  return MEMBERS.reduce((sum, m) => sum + Number(amounts[m]), 0)
}

export default function AllocationHistory() {
  const maxMd = useMaxMd()
  const { canEdit } = usePageAccess('distributions')
  const numColClass = maxMd ? MOBILE_NUM_COL_CLASS : NUM_COL_CLASS
  const noteColClass = maxMd ? MOBILE_NOTE_TOGGLE_COL_CLASS : NOTE_COL_CLASS
  const cellPadClass = maxMd ? 'px-2 py-2' : 'px-4 py-2'
  const stickyPeriodClass = maxMd ? STICKY_PERIOD_MOBILE_CLASS : STICKY_PERIOD_CLASS
  const stickyPeriodHeadClass = maxMd ? STICKY_PERIOD_HEAD_MOBILE_CLASS : STICKY_PERIOD_HEAD_CLASS
  const stickyPeriodTotalsClass = maxMd ? STICKY_PERIOD_TOTALS_MOBILE_CLASS : STICKY_PERIOD_TOTALS_CLASS
  const [rows, setRows] = useState<AllocationResponse[]>([])
  const [monthsSummary, setMonthsSummary] = useState<WaterfallJeMonthsSummaryMonth[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [activeJeMonth, setActiveJeMonth] = useState<string | null>(null)
  const [jeLoadingNum, setJeLoadingNum] = useState<number | null>(null)

  const monthsSummaryByMonth = useMemo(
    () => new Map(monthsSummary.map((entry) => [entry.report_month, entry])),
    [monthsSummary],
  )

  const loadMonthsSummary = useCallback(async () => {
    try {
      const data = await getWaterfallJournalEntryMonthsSummary('income')
      setMonthsSummary(data.months)
    } catch {
      setMonthsSummary([])
    }
  }, [])

  const refreshRows = useCallback(async () => {
    try {
      const data = await getAllocations()
      setRows(data.allocations)
      await loadMonthsSummary()
      setError(null)
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to refresh allocation history'))
    }
  }, [loadMonthsSummary])

  const loadHistory = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getAllocations()
      setRows(data.allocations)
      await loadMonthsSummary()
    } catch (e) {
      setError(formatApiError(e, 'Failed to load allocation history'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [loadMonthsSummary])

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  const cumulativeAllocations = useMemo(() => {
    const byMember = MEMBERS.reduce((acc, m) => {
      acc[m] = 0
      return acc
    }, {} as MemberAmounts)
    let total = 0
    for (const row of rows) {
      for (const m of MEMBERS) {
        byMember[m] += Number(row.allocation[m])
      }
      total += memberTotal(row.allocation)
    }
    return { byMember, total }
  }, [rows])

  const handleJeRowClick = useCallback(
    async (row: AllocationResponse) => {
      const jeStatus = row.je_status ?? 'none'
      const month = monthFromIsoDate(row.period_end)

      if (jeStatus === 'posted') {
        if (row.je_qbo_url) {
          window.open(row.je_qbo_url, '_blank', 'noopener,noreferrer')
        } else {
          toast.error('QuickBooks link is not available for this journal entry')
        }
        return
      }

      if (jeStatus === 'draft' || jeStatus === 'stale') {
        setActiveJeMonth(month)
        return
      }

      if (!canEdit) return

      setJeLoadingNum(row.num)
      try {
        await generateWaterfallJournalEntry({ mode: 'income', month })
        setActiveJeMonth(month)
        await refreshRows()
        toast.success('Journal entry draft created')
      } catch (e) {
        const parsed = parseJeGenerateError(e)
        toast.error(parsed.message)
      } finally {
        setJeLoadingNum(null)
      }
    },
    [canEdit, refreshRows],
  )

  const handleDeleteLatest = async () => {
    setDeleting(true)
    setError(null)
    try {
      await deleteLatestAllocation()
      setConfirmDeleteOpen(false)
      if (activeJeMonth && rows.length > 0) {
        const latestMonth = monthFromIsoDate(rows[rows.length - 1]!.period_end)
        if (activeJeMonth === latestMonth) {
          setActiveJeMonth(null)
        }
      }
      await loadHistory()
    } catch (e) {
      const message = formatApiError(e, 'Failed to delete latest allocation')
      setError(message)
      toast.error(message)
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500">Loading allocation history…</p>
  }

  if (error) {
    return (
      <div className="space-y-3">
        <WaterfallTabHeader
          title="History"
          description="Logged income allocation periods — separate from cash distribution history."
        />
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
        <button
          type="button"
          onClick={() => void loadHistory()}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="shrink-0">
        <WaterfallTabHeader
          title="History"
          description="Logged income allocation periods — separate from cash distribution history."
        />
      </div>

      {activeJeMonth != null ? (
        <div className="mb-4 shrink-0">
          <WaterfallJournalEntryCard
            mode="income"
            month={activeJeMonth}
            showReadinessChecklist={false}
            canPostJe={monthsSummaryByMonth.get(activeJeMonth)?.can_post_je ?? true}
            onDismiss={() => setActiveJeMonth(null)}
            onChanged={() => void refreshRows()}
          />
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          No income allocations logged yet. Use the Calculator to complete month readiness,
          calculate the split, and log.
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 py-2">
            <p className="text-sm text-gray-500">
              {rows.length} allocation period{rows.length === 1 ? '' : 's'} logged
            </p>
            {canEdit && (
              <button
                type="button"
                onClick={() => setConfirmDeleteOpen(true)}
                className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50"
              >
                Delete latest
              </button>
            )}
          </div>
          <div className="table-scroll-mobile min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain">
            <table className="min-w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr>
                  <th
                    className={`${STICKY_NUM_HEAD_CLASS} ${cellPadClass} text-left font-medium text-gray-600`}
                  >
                    #
                  </th>
                  <th
                    className={`${stickyPeriodHeadClass} ${cellPadClass} text-left font-medium text-gray-600`}
                  >
                    Period
                  </th>
                  <th
                    className={`${JE_COL_CLASS} ${STICKY_HEAD_CLASS} ${cellPadClass} text-left font-medium text-gray-600`}
                  >
                    JE
                  </th>
                  {MEMBERS.map((m) => (
                    <th
                      key={m}
                      className={`${numColClass} ${STICKY_HEAD_CLASS} ${cellPadClass} text-right font-medium text-gray-600`}
                    >
                      {m}
                    </th>
                  ))}
                  <th
                    className={`${numColClass} ${STICKY_HEAD_CLASS} ${cellPadClass} text-right font-medium text-gray-600 ${TOTAL_COL_BORDER}`}
                  >
                    Total
                  </th>
                  <th
                    className={`${STICKY_HEAD_CLASS} ${noteColClass} ${cellPadClass} text-left font-medium text-gray-600`}
                  >
                    {maxMd ? <span className="sr-only">Note</span> : 'Note'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr aria-label="Cumulative allocation totals (locked)">
                  <td className={`${STICKY_NUM_TOTALS_CLASS} ${cellPadClass} font-bold`} />
                  <td
                    className={`${stickyPeriodTotalsClass} ${cellPadClass} font-bold text-gray-900`}
                  >
                    Total allocated
                  </td>
                  <td className={`${JE_COL_CLASS} ${STICKY_TOTALS_CLASS} ${cellPadClass}`} />
                  {MEMBERS.map((m) => (
                    <td
                      key={m}
                      className={`${numColClass} ${STICKY_TOTALS_CLASS} ${cellPadClass} text-right tabular-nums font-bold ${allocationAmountClassName(cumulativeAllocations.byMember[m])}`}
                    >
                      {fmt.format(cumulativeAllocations.byMember[m])}
                    </td>
                  ))}
                  <td
                    className={`${numColClass} ${STICKY_TOTALS_CLASS} ${cellPadClass} text-right tabular-nums font-bold text-gray-900 ${TOTAL_COL_BORDER}`}
                  >
                    {fmt.format(cumulativeAllocations.total)}
                  </td>
                  <td className={`${STICKY_TOTALS_CLASS} ${noteColClass} ${cellPadClass} font-bold`} />
                </tr>
                {rows.map((row) => {
                  const isLatest = row.num === rows[rows.length - 1]?.num
                  return (
                    <tr
                      key={row.id}
                      className={`group ${isLatest ? 'bg-amber-50/40 hover:bg-amber-50/60' : 'hover:bg-gray-50'}`}
                    >
                      <td
                        className={`${STICKY_NUM_CLASS} ${cellPadClass} text-gray-700 ${
                          isLatest ? 'bg-amber-50/40 group-hover:bg-amber-50/60' : ''
                        }`}
                      >
                        {row.num}
                      </td>
                      <td
                        className={`${stickyPeriodClass} ${cellPadClass} text-gray-700 ${
                          isLatest ? 'bg-amber-50/40 group-hover:bg-amber-50/60' : ''
                        }`}
                      >
                        {row.period_start} – {row.period_end}
                      </td>
                      <td className={`${JE_COL_CLASS} ${cellPadClass}`}>
                        {canEdit || row.je_status !== 'none' ? (
                          <DistributionJeRowButton
                            status={row.je_status}
                            loading={jeLoadingNum === row.num}
                            onClick={() => void handleJeRowClick(row)}
                          />
                        ) : null}
                      </td>
                      {MEMBERS.map((m) => (
                        <td
                          key={m}
                          className={`${numColClass} ${cellPadClass} text-right tabular-nums ${allocationAmountClassName(Number(row.allocation[m]))}`}
                        >
                          {fmt.format(Number(row.allocation[m]))}
                        </td>
                      ))}
                      <td
                        className={`${numColClass} ${cellPadClass} text-right tabular-nums font-medium ${TOTAL_COL_BORDER}`}
                      >
                        {fmt.format(memberTotal(row.allocation))}
                      </td>
                      <td className={`${noteColClass} ${cellPadClass} align-top text-gray-700`}>
                        {maxMd ? (
                          row.note?.trim() ? (
                            <span
                              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-brand-600"
                              title={row.note}
                              aria-label={`Note: ${row.note}`}
                            >
                              •
                            </span>
                          ) : null
                        ) : (
                          <span className="block truncate" title={row.note ?? undefined}>
                            {row.note ?? ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canEdit && confirmDeleteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--overlay-standard)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Delete latest allocation?</h3>
            <p className="mt-2 text-sm text-gray-600">
              This removes allocation #{rows[rows.length - 1]?.num} (
              {rows[rows.length - 1]?.period_start} – {rows[rows.length - 1]?.period_end}). Only use
              this if the books for that period are not yet closed.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDeleteOpen(false)}
                disabled={deleting}
                className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleDeleteLatest()}
                disabled={deleting}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Yes, delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
